import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSEO } from '../../utils/seo';

interface MaintenanceItem {
  title: string;
  description: string;
  details: string[];
  image: string;
}

export default function ManutenzionePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useSEO({
    title: 'Manutenzione Auto e Moto a Brescia e Provincia - Batteria, Lampadine | Metamorfosi Preparazioni',
    description: 'Manutenzione ordinaria auto e moto a domicilio a Brescia e provincia. Sostituzione batteria, lampadine, spazzole tergicristallo, rabbocco liquidi, controllo pressione pneumatici e fusibili. Piccoli interventi che fanno la differenza a Brescia.',
    keywords: 'manutenzione auto Brescia, manutenzione moto Brescia, batteria auto Brescia, lampadine auto Brescia, spazzole tergicristallo Brescia, rabbocco liquidi Brescia, pressione pneumatici Brescia, meccanico Brescia provincia',
    canonical: '/manutenzione',
    ogTitle: 'Manutenzione Auto e Moto a Brescia - Servizio a Domicilio',
    ogDescription: 'Piccoli interventi di manutenzione a Brescia e provincia',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      serviceType: 'Manutenzione Ordinaria Auto e Moto a Brescia',
      provider: {
        '@type': 'AutoRepair',
        name: 'Metamorfosi Preparazioni',
        url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`,
        telephone: '+39-351-831-2394',
        email: 'metamorfosi.preparazioni@gmail.com'
      },
      areaServed: {
        '@type': 'City',
        name: 'Brescia',
        containedIn: {
          '@type': 'AdministrativeArea',
          name: 'Provincia di Brescia'
        }
      },
      description: 'Manutenzione ordinaria per auto e moto a domicilio a Brescia e provincia. Include sostituzione batteria, lampadine, spazzole tergicristallo, rabbocco liquidi, controllo pressione pneumatici e sostituzione fusibili.',
      offers: {
        '@type': 'Offer',
        availability: 'https://schema.org/InStock',
        priceRange: '$'
      }
    }
  });

  const maintenanceItems: MaintenanceItem[] = [
    {
      title: 'Sostituzione Batteria',
      description: 'La batteria è il cuore elettrico del tuo veicolo. Fornisce l\'energia necessaria per l\'avviamento e alimenta tutti i sistemi elettronici quando il motore è spento.',
      details: [
        'Avviamento sicuro anche nelle giornate fredde',
        'Alimentazione stabile per elettronica di bordo',
        'Test di carica e stato di salute incluso',
        'Durata media: 4-6 anni'
      ],
      image: 'https://public.readdy.ai/ai/img_res/4553700e30b87745149aed1a20cbebdc.jpg'
    },
    {
      title: 'Sostituzione Lampadine',
      description: 'L\'illuminazione è fondamentale per la sicurezza stradale. Sostituiamo tutti i tipi di lampadine: fari anteriori, posteriori, frecce, luci di posizione e plafoniere.',
      details: [
        'Fari anabbaglianti e abbaglianti',
        'Luci di posizione e stop',
        'Frecce e luci di retromarcia',
        'Lampadine LED e alogene disponibili'
      ],
      image: 'https://readdy.ai/api/search-image?query=Automotive%20car%20headlight%20bulbs%20halogen%20and%20LED%20bulbs%20various%20sizes%20professional%20spare%20parts%20clean%20white%20background%20product%20photography%20high%20detail%20vehicle%20lighting%20maintenance%20replacement%20parts&width=600&height=400&seq=bulbs001&orientation=landscape'
    },
    {
      title: 'Sostituzione Spazzole Tergicristallo',
      description: 'Una visibilità perfetta è essenziale per la sicurezza. Le spazzole tergicristallo si usurano con il tempo e perdono efficacia, lasciando aloni e strisce sul parabrezza.',
      details: [
        'Pulizia ottimale del parabrezza',
        'Eliminazione di aloni e strisce',
        'Spazzole anteriori e posteriori',
        'Intervallo consigliato: ogni 12 mesi'
      ],
      image: 'https://readdy.ai/api/search-image?query=New%20windshield%20wiper%20blades%20car%20wipers%20black%20rubber%20blade%20automotive%20spare%20part%20clean%20product%20photography%20white%20background%20high%20detail%20vehicle%20maintenance%20accessory%20rain%20visibility%20safety&width=600&height=400&seq=wipers001&orientation=landscape'
    },
    {
      title: 'Rabbocco Liquidi',
      description: 'I liquidi del veicolo sono vitali per il corretto funzionamento di tutti i sistemi. Controlliamo e rabbocchiamo olio motore, liquido refrigerante, lavavetri e liquido freni.',
      details: [
        'Liquido refrigerante per il raffreddamento motore',
        'Liquido lavavetri per la pulizia parabrezza',
        'Liquido servosterzo per sterzo fluido',
        'Controllo livelli incluso nel servizio'
      ],
      image: 'https://readdy.ai/api/search-image?query=Automotive%20fluids%20bottles%20coolant%20washer%20fluid%20brake%20fluid%20colorful%20liquids%20car%20maintenance%20products%20professional%20clean%20white%20background%20product%20photography%20high%20detail%20vehicle%20service%20supplies&width=600&height=400&seq=fluids001&orientation=landscape'
    },
    {
      title: 'Controllo Pressione Pneumatici',
      description: 'La corretta pressione degli pneumatici garantisce sicurezza, comfort di guida e risparmio di carburante. Pneumatici sgonfi aumentano consumi e usura irregolare.',
      details: [
        'Verifica pressione su tutte e 4 le ruote',
        'Gonfiaggio alla pressione consigliata',
        'Controllo visivo stato pneumatici',
        'Consigliato: controllo mensile'
      ],
      image: 'https://readdy.ai/api/search-image?query=Professional%20tire%20pressure%20gauge%20checking%20car%20tire%20pressure%20automotive%20maintenance%20tool%20digital%20gauge%20black%20rubber%20tire%20close%20up%20clean%20background%20high%20detail%20vehicle%20safety%20service%20equipment&width=600&height=400&seq=tirepressure001&orientation=landscape'
    },
    {
      title: 'Sostituzione Fusibili',
      description: 'I fusibili proteggono i circuiti elettrici del veicolo da sovraccarichi e cortocircuiti. Un fusibile bruciato può causare il malfunzionamento di luci, radio o altri accessori.',
      details: [
        'Diagnosi del circuito interessato',
        'Sostituzione con fusibile corretto',
        'Verifica assenza cortocircuiti',
        'Ripristino funzionalità immediato'
      ],
      image: 'https://readdy.ai/api/search-image?query=Automotive%20car%20fuses%20colorful%20blade%20fuses%20various%20amperage%20red%20yellow%20blue%20green%20electrical%20components%20spare%20parts%20clean%20white%20background%20product%20photography%20high%20detail%20vehicle%20electrical%20maintenance&width=600&height=400&seq=fuses001&orientation=landscape'
    }
  ];

  const navigate = useNavigate();

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('contatti');
      if (element) {
        const offset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      }
    }, 300);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black shadow-lg">
        <div className="w-full px-4 sm:px-6 lg:px-12">
          <div className="flex items-center justify-between h-16 sm:h-18 md:h-20">
            <Link to="/" className="flex-shrink-0">
              <img 
                src="https://static.readdy.ai/image/26c3401126eecf15eb2286ded28883ff/289e4af9c1f47a64ff4db8c7e8c98e4a.jpeg" 
                alt="Metamorfosi Preparazioni Logo" 
                className="h-10 sm:h-12 md:h-14 w-auto"
              />
            </Link>
            <Link 
              to="/" 
              className="flex items-center text-white hover:text-[#E31E24] transition-colors duration-300 cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              <span className="font-semibold text-sm">Torna alla Home</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-gray-100 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <div className="inline-block mb-4">
            <span className="inline-block px-6 py-2 border-2 border-[#E31E24] text-[#E31E24] bg-white font-bold text-xs tracking-widest rounded-full uppercase">
              Piccoli Interventi
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-black mb-6">
            Manutenzione Ordinaria
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Piccoli interventi che fanno la differenza. Dalla sostituzione delle lampadine al rabbocco dei liquidi, ci prendiamo cura di ogni dettaglio del tuo veicolo.
          </p>
        </div>
      </section>

      {/* Maintenance Items Detail */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="space-y-16 md:space-y-24">
            {maintenanceItems.map((item, index) => (
              <div 
                key={index}
                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-8 lg:gap-16`}
              >
                {/* Image */}
                <div className="w-full lg:w-1/2">
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
                    <div className="w-full h-64 sm:h-80 md:h-96">
                      <img 
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                  </div>
                </div>

                {/* Content */}
                <div className="w-full lg:w-1/2">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 rounded-full bg-[#E31E24] flex items-center justify-center mr-4">
                      <i className="ri-checkbox-circle-line text-white text-2xl"></i>
                    </div>
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-black">
                      {item.title}
                    </h2>
                  </div>
                  <p className="text-base sm:text-lg text-gray-600 mb-6 leading-relaxed">
                    {item.description}
                  </p>
                  <ul className="space-y-3">
                    {item.details.map((detail, detailIndex) => (
                      <li key={detailIndex} className="flex items-start">
                        <div className="w-6 h-6 rounded-full bg-[#E31E24]/10 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                          <i className="ri-check-line text-[#E31E24] text-sm"></i>
                        </div>
                        <span className="text-gray-700 text-sm sm:text-base">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-24 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 md:mb-6">
            Richiedi un Intervento
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-white/80 mb-6 md:mb-8 max-w-2xl mx-auto">
            Contattaci per qualsiasi piccolo intervento di manutenzione. Veniamo direttamente da te, a casa o in ufficio.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button 
              onClick={scrollToContact}
              className="inline-flex items-center bg-[#E31E24] text-white font-bold text-sm px-8 py-4 rounded-full hover:bg-[#c41a1f] transition-all duration-300 hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
            >
              <span className="mr-3">RICHIEDI PREVENTIVO</span>
              <i className="ri-arrow-right-line text-lg"></i>
            </button>
            <a 
              href="https://wa.me/393518312394"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-white text-black font-bold text-sm px-8 py-4 rounded-full hover:bg-gray-100 transition-all duration-300 hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-[#25D366] text-xl mr-3"></i>
              <span>WHATSAPP</span>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#E31E24] py-6 md:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex flex-col items-center space-y-4">
            <Link to="/" className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/26c3401126eecf15eb2286ded28883ff/289e4af9c1f47a64ff4db8c7e8c98e4a.jpeg" 
                alt="Metamorfosi Preparazioni Logo" 
                className="h-10 w-auto"
              />
            </Link>
            <div className="flex items-center">
              <i className="ri-mail-line text-white text-lg mr-2"></i>
              <a href="mailto:metamorfosi.preparazioni@gmail.com" className="text-white/90 hover:text-white transition-colors duration-300 cursor-pointer text-xs sm:text-sm break-all">
                metamorfosi.preparazioni@gmail.com
              </a>
            </div>
            <p className="text-white/80 text-xs sm:text-sm text-center">
              © 2025 Metamorfosi Preparazioni. Tutti i diritti riservati.
            </p>
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-white/70 hover:text-white text-xs sm:text-sm transition-colors duration-300 cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
